import {get, pick, filter} from "lodash";
import { thor } from "../../../typings/thor";
import { Enums, Events } from "../../../thor.enums";
import { LocaleCode } from "../../../typings/lang";

export class ItemEditController {
    public static $inject: string[] = [
        "$log",
        "$mdDialog",
        "$mdMedia",
        "$rootScope",
        "$scope",
        "$translate",
        "categoryService",
        "hotkeys",
        "itemEdit",
        "itemService",
        "mediaGalleryService",
        "toaster",
        "utilsService"
    ];

    // Binded
    public getTranslationStatus: Function;
    public isSaving: boolean;
    public selectedTargetLanguage: LocaleCode;
    public sourceLang: LocaleCode;

    public fields: Array<any> = [];
    public form;
    public originalItem;
    public showTooltip: boolean = false;
    public saleCurrencySymbol: Enums.Currency;

    private patchedDatas: thor.ItemPatch = {};
    private $t;

    constructor(
        private $log: ng.ILogService,
        private $mdDialog: ng.material.IDialogService,
        public $mdMedia: ng.material.IMedia,
        private $rootScope: ng.IRootScopeService,
        $scope: ng.IScope,
        $translate: ng.translate.ITranslateService,
        private categoryService,
        private hotkeys,
        private itemEdit,
        private itemService,
        private mediaGalleryService,
        private toaster,
        private utilsService
    ) {
        this.$t = $translate.instant;
        this.fields = this.categoryService.getFields();

        $scope.$watch(
            () => {
                return this.itemEdit.isOpen;
            },
            isOpen => {
                if (isOpen) {
                    if (this.form) {
                        this.form.orderNumberPrimary.$setValidity("uniqueOrderNumber", true);
                    }
                    this._autoFocus();
                    this._bindHotkeys();
                } else {
                    this._unbindHotkeys();
                }
        });
    }

    public canSave() {
        return !(this.isSaving || angular.equals(this.patchedDatas, {}));
    }

    public checkEstimateValidity() {
        if (!this.form) {
            return;
        }
        const min = get(this.itemEdit.currentItem, "pricing.estimates.min");
        const max = get(this.itemEdit.currentItem, "pricing.estimates.max");
        this.form.estimateMax.$setValidity("estimateValue", !min || !max || min <= max);

        this.patchPricing();
    }

    public close() {
        this.showTooltip = false;

        if (angular.equals(this.patchedDatas, {})) {
            this.itemEdit.resetCurrentItem();
            this.itemEdit.close();
        } else {
            const confirm = this.$mdDialog.confirm()
                .title(this.$t("fermer_sans_sauvegarder"))
                .textContent(this.$t("ce_lot_a_ete_modifie_souhaitez_vous_fermer_sans_sauvegarder_les_modifications"))
                .ariaLabel(this.$t("fermer_un_lot_non_sauvegarde"))
                .ok(this.$t("oui"))
                .cancel(this.$t("annuler"));
            this.$mdDialog.show(confirm)
                .then(
                    () => {
                        this.patchedDatas = {};
                        this.itemEdit.resetCurrentItem();
                        this.itemEdit.close();
                    });
        }
    }

    public next() {
        if (this.itemEdit.nextItem) {
            this.save()
                .then(() => {
                    this.itemEdit.next();
                    this._autoFocus();
                });
        }
    }

    public previous() {
        if (this.itemEdit.previousItem) {
            this.save().then(() => {
                this.itemEdit.previous();
                this._autoFocus();
            });
        }
    }

    public patchCategory() {
        if (
            angular.equals(
                this.itemEdit.originalItem.category.id,
                this.itemEdit.currentItem.category.id
            )
        ) {
            delete this.patchedDatas.category_id;
        } else {
            this.patchedDatas.category_id = this.itemEdit.currentItem.category.id;
        }
    }

    public patchDescription() {
        if (angular.equals(
                this.itemEdit.originalItem.description,
                this.itemEdit.currentItem.description
            )
        ) {
            delete this.patchedDatas.description;
        } else {
            this.patchedDatas.description = this.itemEdit.currentItem.description;
        }
    }

    public patchDescriptionTranslation() {
        if (
            angular.equals(
                this.itemEdit.originalItem.description_translations[this.selectedTargetLanguage],
                this.itemEdit.currentItem.description_translations[this.selectedTargetLanguage]
            )
        ) {
            delete this.patchedDatas.description_translations;
        } else {
            this.patchedDatas.description_translations = {
                [this.selectedTargetLanguage]:
                    this.itemEdit.currentItem.description_translations[this.selectedTargetLanguage]
            };
        }
    }

    public patchInternalReference() {
        if (
            angular.equals(
                this.itemEdit.currentItem.internal_reference,
                this.itemEdit.originalItem.internal_reference
            )
        ) {
            delete this.patchedDatas.internal_reference;
        } else {
            this.patchedDatas.internal_reference = this.itemEdit.currentItem.internal_reference;
        }
    }

    public patchOrderNumber() {
        const originalMeta = pick(this.itemEdit.originalItem.meta, "order_number");
        const currentMeta: any = pick(this.itemEdit.currentItem.meta, "order_number");

        const items = filter(
            angular.copy(this.itemEdit.items),
            (item: any) => item.id !== this.itemEdit.currentItem.id
        );

        const sameItemFound = this.utilsService.findItemByOrderNumber(
            currentMeta.order_number,
            items
        );

        this.form.orderNumberPrimary.$setValidity("uniqueOrderNumber", !sameItemFound);

        if (
            angular.equals(currentMeta, originalMeta) || sameItemFound
        ) {
            delete this.patchedDatas.meta;
        } else {
            this.patchedDatas.meta = currentMeta;
        }
    }

    public patchPricing() {
        const originalPricing = pick(this.itemEdit.originalItem.pricing, "estimates");
        let currentPricing = pick(this.itemEdit.currentItem.pricing, "estimates");

        if (
            !get(currentPricing, "estimates.min") &&
            !get(currentPricing, "estimates.max")
        ) {
            currentPricing = null;
        }

        if (
            angular.equals(
                currentPricing,
                originalPricing
            )
        ) {
            delete this.patchedDatas.pricing;
        } else {
            this.patchedDatas.pricing = currentPricing;
        }
    }

    public saveAndClose() {
        this.showTooltip = false;

        this.save()
        .then(() => {
            this.itemEdit.close();
        });
    }

    public async save() {
        this.isSaving = true;

        if (!angular.equals(this.patchedDatas, {})) {
            if (get(this.patchedDatas, "meta.order_number.secondary") &&
                !get(this.patchedDatas, "meta.order_number.primary")
            ) {
                this.patchedDatas.meta.order_number.secondary = null;
            }

            return this._saveItem()
                .then(() => {
                    if (!angular.equals(this.itemEdit.currentItem, this.itemEdit.originalItem)) {
                        this.toaster.toast({
                            message: this.$t("lot_sauvegarde"),
                            delay: 2000
                        });
                        this.$rootScope.$broadcast(Events.ItemUpdated, this.itemEdit.currentItem);
                        this.$rootScope.$broadcast(Events.PostMessageToV2);
                        this.itemEdit.originalItem = angular.copy(this.itemEdit.currentItem);
                    }
                })
                .finally(() => {
                    this.patchedDatas = {};
                    this.isSaving = false;
                });
        } else {
            this.itemEdit.resetCurrentItem();
            this.isSaving = false;
        }
    }

    public showGallery() {
        this.mediaGalleryService.open(this.itemEdit.currentItem, "gallery");
    }

    private async _saveItem() {
        try {
            let updatedTranslations = {};

            if (this.patchedDatas.description_translations) {
                updatedTranslations = await this.handleDescriptionTranslations();

                if (this.patchedDatas.description_translations) {
                    delete this.patchedDatas.description_translations;
                }
            }

            if (!angular.equals(this.patchedDatas, {})) {
                const res = await this.itemService.update(this.itemEdit.currentItem.id, this.patchedDatas);
                this.$log.info("Item saved ✓", res);
                await this.getTranslationStatus();
                if (!angular.equals(updatedTranslations, {})) {
                    this.itemEdit.currentItem.description_translations = updatedTranslations;
                }
                this.itemEdit.currentItem = (<any>Object).assign(this.itemEdit.currentItem, res);
            }
        } catch (error) {
            this.$log.error("Error while updating the item", error);
            this.toaster.show(this.$t("une_erreur_est_survenue"), true);
        }
    }

    private handleDescriptionTranslations() {
        const descriptionTranslations = {
            description_translations: this.patchedDatas.description_translations
        };
        this.isSaving = true;

        return this.itemService
            .translate(this.itemEdit.currentItem.id, descriptionTranslations)
            .then(async res => {
                this.$log.info("Item translation saved ✓", res);
                await this.getTranslationStatus();
                return res.description_translations;
            })
            .finally(() => (this.isSaving = false));
    }

    private _autoFocus() {
        setTimeout(
            () => {
                angular.element(document.querySelector("input[auto-focus]")).focus();
            },
            200
        );
    }

    private _bindHotkeys() {
        this.hotkeys
            .add({
                combo: "mod+down",
                description: this.$t("lot_suivant"),
                allowIn: ["INPUT", "SELECT", "TEXTAREA"],
                callback: e => {
                    e.preventDefault();
                    this.save()
                        .then(() => {
                            this.next();
                        });
                }
            });

        this.hotkeys
            .add({
                combo: "mod+up",
                description: this.$t("lot_precedent"),
                allowIn: ["INPUT", "SELECT", "TEXTAREA"],
                callback: e => {
                    e.preventDefault();
                    this.save()
                        .then(() => {
                            this.previous();
                        });
                }
            });

        this.hotkeys
            .add({
                combo: "mod+s",
                description: this.$t("sauvegarde_le_lot"),
                allowIn: ["INPUT", "SELECT", "TEXTAREA"],
                callback: e => {
                    e.preventDefault();
                    this.save();
                }
            });

        this.hotkeys
            .add({
                combo: "esc",
                description: this.$t("ferme_le_mode_edition"),
                allowIn: ["INPUT", "SELECT", "TEXTAREA"],
                callback: e => {
                    e.preventDefault();
                    this.close();
                }
            });
    }

    private _unbindHotkeys() {
        this.hotkeys.del("mod+up");
        this.hotkeys.del("mod+down");
        this.hotkeys.del("mod+s");
        this.hotkeys.del("esc");
    }
}
